'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import JsonLd from '@src/components/Shared/JsonLd';
import NextImage from '@src/components/Shared/NextImage';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import InspectorWrapper from '@src/components/Shared/InspectorWrapper';

import TrustedbyStats from '@src/components/Sections/TrustedbyStats';
import FeaturedGrid from '@src/components/Sections/FeatureGrid';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import Testimonials from '@src/components/Sections/Testimonials';
import ServiceOverview from '@src/components/Sections/ServiceOverview';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { HomePageClientPropTypes } from './HomePage.types';
import HeroPrimary from '@src/components/Shared/HeroPrimary';

const HomePageClient: React.FC<HomePageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(constructURL({ languageCode: page?.languageCode, slug: '/', baseURL: '' }));
  setContentType('site-page');

  const homeJsonLd = JsonLd({
    name: page?.pageName,
    pageUrl: `/${page?.languageCode?.toLowerCase()}${page?.slug === '/' ? '' : page?.slug}`,
    thumbnailUrl: page?.seoMetadata?.previewImage?.url,
    description: page?.seoMetadata?.description,
    datePublished: page?.sys?.firstPublishedAt,
    dateModified: page?.sys?.publishedAt,
    locale: page?.languageCode?.toLowerCase(),
  });

  return (
    <>
      <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(homeJsonLd) }} />

      <HeroPrimary
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#070707]'
        attr='hero-component'
        className='bg-background-primary! mb-[8rem] flex flex-col items-center justify-between overflow-hidden pt-[6rem] pb-[3rem] md:flex-row md:overflow-visible md:px-[4rem]! md:pt-[9.3rem] md:pb-[6.3rem] lg:gap-[2rem] lg:pt-[11.2rem] lg:pb-[8.2rem] 2xl:h-[75rem]! 2xl:py-[9.6rem]'
        title={page?.hero?.items?.[0]?.title}
        bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        image={page?.hero?.items?.[0]?.mediaPrimary}
        titleClassName='text-[4rem] lg:text-[6rem] 2xl:tracking-[-0.2432rem] leading-[4.7rem] lg:leading-[6.9rem] md:max-w-[36.6rem] md:max-w-3xl lg:max-w-[50rem] 2xl:max-w-full 2xl:tracking-[-0.152rem]'
        imgClassName='absolute! sm:relative! flex items-end md:items-center justify-end xl:max-h-[80%] top-[0] h-full md:w-[50%] opacity-[0.2] sm:opacity-[1] [&>img]:w-auto! md:[&>img]:max-w-[110%]! md:[&>img]:right-[-10%] &>img]:lg:right-[0%] md:[&>img]:absolute!'
        bodyCopyClassName='[&>p]:text-[1.6rem] md:[&>p]:text-[1.8rem] lg:[&>p]:text-[2rem] 2xl:[&>p]:text-[2rem] [&>p]:leading-[150%] md:max-w-[34.2rem] md:max-w-[43.7rem] lg:max-w-[51.7rem] 2xl:max-w-[59rem] relative md:top-[0] mt-[1.6rem]'
        childrenClassName='xsm:max-w-full'
      />

      <Container
        id={page?.forEveryTeam?.items?.[0]?.scrollTo?.slice(1)}
        className='py-[2rem] md:py-[4rem] 2xl:py-[6rem]'
      >
        <SectionHeader
          entryId={page?.forEveryTeam?.items?.[0]?.sys?.id}
          preTitleClassName='mb-[1.6rem]'
          title={page?.forEveryTeam?.items?.[0]?.title}
          titleClassName='text-left md:text-center mt-[0] mb-[4rem] md:my-[4rem] text-[2.2rem] leading-[2.6rem] tracking-[-1px] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] 2xl:max-w-[100rem] lg:text-[4.8rem] 2xl:text-[6.2rem] lg:leading-[5rem] 2xl:leading-[6.5rem]'
          childrenClassName='relative'
          align='center'
        >
          <RenderBodyCopy
            entryId={page?.hero?.items?.[0]?.sys?.id}
            className='mx-auto max-w-[51.7rem] text-left md:text-center 2xl:max-w-[78.2rem]'
            bodyCopy={page?.forEveryTeam?.items?.[0]?.bodyCopy?.json?.content}
          />
        </SectionHeader>
        {/* TODO: Change this entry from Duplex To SectionHeader */}
      </Container>

      <TrustedbyStats data={page?.trustedBy?.items?.[0]} />

      <Container
        id={page?.siteOptions?.items?.[0]?.scrollTo?.slice(1)}
        className='mt-[8rem] py-[2rem] pt-[0rem] md:mt-[4rem] md:py-[4rem] xl:mt-[0] 2xl:py-[6rem]'
      >
        <div className='grid md:mb-[0] md:grid-cols-4 lg:mx-auto lg:max-w-[130rem] lg:gap-[2.4rem]'>
          {data?.page?.siteOptions?.items?.[0]?.topicCollection?.items?.map((site, index) => {
            return (
              <div
                key={`${site?.sys?.id}-site-option`}
                className={`relative mb-[4rem] flex flex-col text-left md:mb-[0rem] md:block md:text-center ${
                  index % 2 === 0 ? 'md:bottom-[3rem]' : 'md:top-[3rem]'
                }`}
              >
                {index % 2 !== 0 && (
                  <NextImage
                    entryId={site.sys?.id}
                    className='mx-auto mb-[2.4rem] w-full max-w-[22.6rem]'
                    src={site.mediaPrimary?.url}
                    alt='A portrait'
                    aspectRatio={site.mediaPrimary?.width / site.mediaPrimary?.height}
                    width={site.mediaPrimary?.width}
                  />
                )}

                <InspectorWrapper entryId={site.sys?.id} fieldId='title'>
                  <p
                    style={{
                      textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
                    }}
                    className='text-primary order-1 mb-[1.6rem] font-medium 2xl:text-[2.8rem] 2xl:leading-[4.2rem]'
                  >
                    {site?.title}
                  </p>
                </InspectorWrapper>

                <RenderBodyCopy
                  entryId={site.sys?.id}
                  className='text-body-copy order-2 font-light'
                  bodyCopy={site?.bodyCopy?.json?.content}
                />

                <RenderButtons
                  items={site?.buttonCollection?.items}
                  className='order-3 md:[&>div]:w-full'
                  btnClassName='justify-start! md:justify-center!'
                />

                {index % 2 === 0 && (
                  <NextImage
                    entryId={site.sys?.id}
                    className='order-0 mx-auto mb-[2.4rem] w-full max-w-[22.6rem] md:mt-[2.4rem] md:mb-[0rem]'
                    src={site.mediaPrimary?.url}
                    alt='A portrait'
                    aspectRatio={site.mediaPrimary?.width / site.mediaPrimary?.height}
                    width={site.mediaPrimary?.width}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Container>

      <Container
        id={page?.strengthInNumbers?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-primary'
        className='py-[5rem] md:py-[9rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] h-full md:mb-[3.6rem]'
          entryId={page?.strengthInNumbers?.items?.[0]?.sys?.id}
          pretitle={page?.strengthInNumbers?.items?.[0]?.preTitle}
          title={page?.strengthInNumbers?.items?.[0]?.title}
          titleClassName='text-left md:text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] 2xl:max-w-[78rem] lg:text-[4.8rem] 2xl:text-[6.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
          childrenClassName='relative'
          align='center'
        >
          <RenderBodyCopy
            entryId={page?.strengthInNumbers?.items?.[0]?.sys?.id}
            className='text-body-copy--light mx-auto mt-[2.4rem] max-w-[51.7rem] text-left font-light md:text-center 2xl:mt-[2rem] 2xl:max-w-[63rem]'
            bodyCopy={page?.strengthInNumbers?.items?.[0]?.bodyCopy?.json?.content}
          />
        </SectionHeader>

        <NextImage
          className='mx-auto max-w-[120rem]'
          entryId={page?.strengthInNumbers?.items?.[0]?.sys?.id}
          src={page?.strengthInNumbers?.items?.[0]?.mediaPrimary?.url}
          alt='A portrait'
          aspectRatio={
            page?.strengthInNumbers?.items?.[0]?.mediaPrimary?.width /
            page?.strengthInNumbers?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.strengthInNumbers?.items?.[0]?.mediaPrimary?.width}
        />
      </Container>

      <Container
        attr='solution-header-component'
        id={page?.features?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[3.6rem]!'
      >
        <SectionHeader
          entryId={page?.features?.items?.[0]?.sys?.id}
          pretitle={page?.features?.items?.[0]?.preTitle}
          title={page?.features?.items?.[0]?.title}
          titleClassName='text-[2.2rem] leading-[2.6rem] mb-[1.8rem]! md:leading-[4.6rem] md:text-[2.8rem] md:leading-none lg:text-[4.8rem] lg:tracking-[-0.096rem] lg:mb-[3.2rem] 2xl:leading-[6.4rem] 2xl:text-[6.2rem] 2xl:tracking-[-0.072rem] 2xl:mb-[2rem]!'
          childrenClassName='text-[1.6rem] leading-[2.2rem] md:leading-[2.4rem] lg:max-w-[74.6rem]'
          align='center'
        >
          <>
            <RenderBodyCopy
              entryId={page?.features?.items?.[0]?.sys?.id}
              bodyCopy={page?.features?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.features?.items?.[0]?.buttonCollection?.items} className='justify-center' />
          </>
        </SectionHeader>
      </Container>

      <FeaturedGrid
        id={page?.features?.items?.[0]?.scrollTo?.slice(1)}
        features={page?.features?.items?.[0]?.topicCollection?.items}
      />

      <Container
        id={page?.inOnePlace?.items?.[0]?.scrollTo?.slice(1)}
        attr='full-feature-component'
        className='flex flex-col'
      >
        <SectionHeader
          className='justify-center lg:mb-[3.4rem] lg:pb-[unset]'
          entryId={page?.inOnePlace?.items?.[0]?.sys?.id}
          pretitle={page?.inOnePlace?.items?.[0]?.preTitle}
          title={page?.inOnePlace?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5.4rem] font-light 2xl:max-w-[100rem] '
          align='center'
        >
          <>
            <RenderBodyCopy
              entryId={page?.inOnePlace?.items?.[0]?.sys?.id}
              bodyCopy={page?.inOnePlace?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.inOnePlace?.items?.[0]?.buttonCollection?.items} className='justify-center' />
          </>
        </SectionHeader>

        <NextImage
          className='-order-1 mx-auto mb-[2.8rem] max-w-[120rem] lg:order-1 lg:mb-[0]'
          objectFit='contain'
          entryId={page?.inOnePlace?.items?.[0]?.sys?.id}
          src={page?.inOnePlace?.items?.[0]?.mediaPrimary?.url}
          alt={page?.inOnePlace?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.inOnePlace?.items?.[0]?.mediaPrimary?.width / page?.inOnePlace?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.inOnePlace?.items?.[0]?.mediaPrimary?.width}
        />
      </Container>

      <ImageTextSecondary
        entryId={page?.normativeData?.items?.[0]?.sys?.id}
        id={page?.normativeData?.items?.[0]?.scrollTo?.slice(1)}
        reversed
        className='overflow-visible! py-[0]! lg:px-[4rem] lg:py-[2rem]'
        imgSecondClassName='lg:w-[65%]!'
        pretitle={page?.normativeData?.items?.[0]?.preTitle}
        title={page?.normativeData?.items?.[0]?.title}
        imgURL={page?.normativeData?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              entryId={page?.normativeData?.items?.[0]?.sys?.id}
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.normativeData?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.normativeData?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />
      <Container className='w-full'>
        <NextImage
          className='hidden sm:hidden md:block lg:block xl:block'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width}
        />
        <NextImage
          className='block sm:block md:hidden lg:hidden xl:hidden'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width}
        />
      </Container>

      <ImageTextSecondary
        entryId={page?.meetOurClients?.items?.[0]?.sys?.id}
        id={page?.meetOurClients?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='overflow-visible!'
        title={page?.meetOurClients?.items?.[0]?.title}
        imgURL={page?.meetOurClients?.items?.[0]?.mediaPrimary?.url}
        imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset] test'
        childrenElements={
          <>
            <RenderBodyCopy
              entryId={page?.meetOurClients?.items?.[0]?.sys?.id}
              bodyCopy={page?.meetOurClients?.items?.[0]?.bodyCopy?.json?.content}
              className='mb-[2.4rem] max-w-[60rem] 2xl:[&>p]:text-[2.2rem] 2xl:[&>p]:leading-[3.3rem]'
            />

            <div className='mb-[2.4rem] grid max-w-[60rem] grid-cols-2 gap-4 gap-y-[2.4rem] lg:mb-[3rem] 2xl:mb-[4rem]'>
              {page?.meetOurClients?.items?.[0]?.extraAssetsCollection?.items?.map((asset, index) => {
                return (
                  <div key={`${asset?.sys?.id}-${index}`} className='space-y-[0.5rem]'>
                    <InspectorWrapper entryId={asset?.sys?.id} fieldId='title'>
                      <p
                        className='text-primary text-[2.4rem] leading-[3.6rem] font-medium tracking-[-0.0264rem] lg:text-[4.8rem] lg:leading-[5.4rem] lg:tracking-[-0.0396rem]'
                        style={{
                          textShadow:
                            'rgb(255, 255, 255) 1px 0px 0px, rgb(255, 255, 255) 0.540302px 0.841471px 0px, rgb(255, 255, 255) -0.416147px 0.909297px 0px, rgb(255, 255, 255) -0.989992px 0.14112px 0px, rgb(255, 255, 255) -0.653644px -0.756802px 0px, rgb(255, 255, 255) 0.283662px -0.958924px 0px, rgb(255, 255, 255) 0.96017px -0.279415px 0px',
                        }}
                      >
                        {asset?.title}
                      </p>
                    </InspectorWrapper>

                    <RenderBodyCopy
                      entryId={asset?.sys?.id}
                      bodyCopy={asset.bodyCopy?.json?.content}
                      className='text-[1.2rem] leading-[-0.0132rem] tracking-[-0.0132] lg:text-[1.6rem]'
                    />
                  </div>
                );
              })}
            </div>
          </>
        }
      />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <NextImage
        id={page?.weExist?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.weExist?.items?.[0]?.mediaPrimary?.url}
        entryId={page?.weExist?.items?.[0]?.sys?.id}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            entryId={page?.weExist?.items?.[0]?.sys?.id}
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={page?.weExist?.items?.[0]?.title}
            titleClassName='text-left text-[2.2rem] w-full leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] sm:text-center md:leading-[3rem] md:text-[2.8rem] lg:max-w-[55rem] 2xl:max-w-[78rem] 2xl:locale-es:max-w-[81rem] lg:text-[4.8rem] 2xl:text-[7.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
            childrenClassName='relative w-full'
            align='center'
          >
            <RenderBodyCopy
              entryId={page?.weExist?.items?.[0]?.sys?.id}
              className='text-body-copy--light mx-auto mt-[2.4rem] max-w-[51.7rem] text-left sm:text-center 2xl:mt-[3.2rem] 2xl:max-w-[78.2rem]'
              bodyCopy={page?.weExist?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons className='justify-center' items={page?.weExist?.items?.[0]?.buttonCollection?.items} />
          </SectionHeader>
        </Container>
      </NextImage>

      <ServiceOverview
        entryId={page?.clientSuccess?.items?.[0]?.sys?.id}
        id={page?.clientSuccess?.items?.[0]?.scrollTo?.slice(1)}
        description={page?.clientSuccess?.items?.[0]?.bodyCopy}
        services={page?.clientSuccess?.items?.[0]?.topicCollection?.items}
      />

      <CallToAction
        entryId={page?.callToAction?.items?.[0]?.sys?.id}
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default HomePageClient;
